import { useRef, useEffect, useState, useCallback } from 'react'
import { Eraser, Upload, Pen } from 'lucide-react'

interface SignaturePadProps {
  onSave: (imageData: string, type: 'draw' | 'upload') => void
  onCancel: () => void
}

export function SignaturePad({ onSave, onCancel }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasContent, setHasContent] = useState(false)
  const [mode, setMode] = useState<'draw' | 'upload'>('draw')

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  const getPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if ('touches' in e) {
      const touch = e.touches[0]
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      }
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }, [])

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = getPos(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    setIsDrawing(true)
  }, [getPos])

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return
    e.preventDefault()
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = getPos(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    setHasContent(true)
  }, [isDrawing, getPos])

  const stopDraw = useCallback(() => {
    setIsDrawing(false)
  }, [])

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    setHasContent(false)
  }

  const handleSaveDraw = () => {
    const canvas = canvasRef.current
    if (!canvas || !hasContent) return
    const dataUrl = canvas.toDataURL('image/png')
    onSave(dataUrl, 'draw')
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) return
    if (file.size > 2 * 1024 * 1024) {
      alert('La imagen no debe superar 2MB')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        // Resize to max 400x200 on a canvas
        const canvas = document.createElement('canvas')
        const maxW = 400
        const maxH = 200
        let w = img.width
        let h = img.height
        if (w > maxW) { h = h * (maxW / w); w = maxW }
        if (h > maxH) { w = w * (maxH / h); h = maxH }
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, w, h)
        ctx.drawImage(img, 0, 0, w, h)
        const dataUrl = canvas.toDataURL('image/png')
        onSave(dataUrl, 'upload')
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-6">
      <h3 className="text-lg font-semibold text-legal-900 dark:text-white mb-4">
        Nueva Firma
      </h3>

      {/* Mode tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setMode('draw')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'draw'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'bg-legal-100 dark:bg-legal-700 text-legal-600 dark:text-legal-300 hover:bg-legal-200 dark:hover:bg-legal-600'
          }`}
        >
          <Pen size={16} />
          Dibujar
        </button>
        <button
          onClick={() => setMode('upload')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'upload'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'bg-legal-100 dark:bg-legal-700 text-legal-600 dark:text-legal-300 hover:bg-legal-200 dark:hover:bg-legal-600'
          }`}
        >
          <Upload size={16} />
          Subir imagen
        </button>
      </div>

      {mode === 'draw' ? (
        <>
          <div className="border-2 border-dashed border-legal-300 dark:border-legal-600 rounded-lg overflow-hidden mb-4">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="w-full cursor-crosshair touch-none"
              style={{ height: '160px' }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
          </div>
          <p className="text-xs text-legal-500 dark:text-legal-400 mb-4">
            Dibuja tu firma con el mouse o el dedo (touch)
          </p>
          <div className="flex gap-3">
            <button
              onClick={clearCanvas}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-legal-300 dark:border-legal-600 text-legal-600 dark:text-legal-300 rounded-lg hover:bg-legal-50 dark:hover:bg-legal-700"
            >
              <Eraser size={16} />
              Limpiar
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-legal-600 dark:text-legal-300 hover:bg-legal-50 dark:hover:bg-legal-700 rounded-lg"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveDraw}
              disabled={!hasContent}
              className="px-6 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Guardar Firma
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="block border-2 border-dashed border-legal-300 dark:border-legal-600 rounded-lg p-8 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition-colors mb-4">
            <Upload size={32} className="mx-auto text-legal-400 dark:text-legal-500 mb-2" />
            <p className="text-sm text-legal-600 dark:text-legal-300">
              Selecciona una imagen de tu firma
            </p>
            <p className="text-xs text-legal-400 dark:text-legal-500 mt-1">
              PNG, JPG (max 2MB)
            </p>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleUpload}
              className="hidden"
            />
          </label>
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-legal-600 dark:text-legal-300 hover:bg-legal-50 dark:hover:bg-legal-700 rounded-lg"
          >
            Cancelar
          </button>
        </>
      )}
    </div>
  )
}
